import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class BuniqueSocialLook extends Component {
  requiredRefs = ['variantSelects', 'individualButtons', 'submitButton', 'status', 'total', 'totalPrice', 'cartLink'];
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.handleSelection();
  }

  updatedCallback() {
    super.updatedCallback();
    this.handleSelection();
  }

  #selected() {
    return Array.from(this.refs.variantSelects ?? []).map((select) => {
      const option = select.selectedOptions[0];
      const piece = select.closest('[data-look-piece]');
      return select.value && option?.dataset.available === 'true' && !option.disabled
        ? { id: select.value, price: Number(option.dataset.price), select, piece }
        : null;
    });
  }

  #money(cents) {
    return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang, {
      style: 'currency', currency: this.dataset.currency || 'EUR',
    }).format(cents / 100);
  }

  handleSelection() {
    const selected = this.#selected();
    const ready = selected.length === 3 && selected.every(Boolean);
    this.refs.submitButton.disabled = this.#busy || !ready;
    this.refs.submitButton.textContent = this.#busy ? this.dataset.loadingMessage : ready ? this.dataset.readyMessage : this.dataset.selectMessage;
    this.refs.total.hidden = !ready;
    if (ready) this.refs.totalPrice.textContent = this.#money(selected.reduce((sum, item) => sum + item.price, 0));
    for (const [index, button] of Array.from(this.refs.individualButtons).entries()) {
      button.disabled = this.#busy || !selected[index];
      if (selected[index]) selected[index].piece.querySelector('[data-piece-price]').textContent = this.#money(selected[index].price);
    }
    for (const select of this.refs.variantSelects) select.disabled = this.#busy;
    this.refs.status.textContent = '';
  }

  handleLook(event) {
    event.preventDefault();
    const selected = this.#selected();
    if (selected.length !== 3 || selected.some((item) => !item)) {
      this.refs.status.textContent = this.dataset.selectMessage;
      Array.from(this.refs.variantSelects).find((select) => !select.value)?.focus();
      return;
    }
    this.#add(selected);
  }

  handleIndividual(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-piece-index]') : null;
    if (!button) return;
    const item = this.#selected()[Number(button.dataset.pieceIndex)];
    if (item) this.#add([item]);
  }

  async #cart() {
    const component = document.querySelector('cart-items-component');
    if (component) {
      await customElements.whenDefined('cart-items-component');
      return component.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(this.dataset.errorMessage);
    return response.json();
  }

  async #add(selected) {
    if (this.#busy) return;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    this.handleSelection();
    let pending;
    let added = false;
    try {
      for (const item of selected) {
        const response = await fetch(`${item.piece.dataset.productUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error(this.dataset.errorMessage);
        const current = await response.json();
        const variant = current.variants.find((entry) => String(entry.id) === item.id);
        if (!variant?.available) {
          const option = item.select.selectedOptions[0];
          option.disabled = true;
          option.dataset.available = 'false';
          item.select.value = '';
          throw new Error(this.dataset.unavailableMessage.replace('__PRODUCT__', item.piece.dataset.productTitle));
        }
        item.price = variant.price;
        item.select.selectedOptions[0].dataset.price = String(variant.price);
      }
      pending = CartLinesUpdateEvent.createPromise();
      const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component')).map((component) => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: selected.map((item) => ({ merchandiseId: item.id, quantity: 1 })),
        promise: pending.promise,
      }));
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: selected.map((item) => ({ id: Number(item.id), quantity: 1 })),
          sections: sectionIds.join(','), sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : this.dataset.errorMessage);
      added = true;
      const cart = await this.#cart();
      pending.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'bunique-social-look', sourceId: this.id, itemCount: selected.length, sections: data.sections, didError: false },
      });
      this.refs.status.textContent = this.dataset.successMessage;
      this.refs.cartLink.hidden = false;
    } catch (error) {
      if (pending) {
        try {
          const cart = await this.#cart();
          pending.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'bunique-social-look', sourceId: this.id, didError: !added } });
        } catch (refreshError) {
          pending.reject(refreshError);
        }
      }
      this.refs.status.textContent = added ? this.dataset.successMessage : error.message || this.dataset.errorMessage;
      if (added) this.refs.cartLink.hidden = false;
      else this.dispatchEvent(new CartErrorEvent({ error: this.refs.status.textContent, code: 'INVALID' }));
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      const message = this.refs.status.textContent;
      this.handleSelection();
      this.refs.status.textContent = message;
    }
  }
}

if (!customElements.get('bunique-social-look')) customElements.define('bunique-social-look', BuniqueSocialLook);
